import {
  Waves, ArrowRight, Check, X, Star, HelpCircle, Shield,
  Lock, Building2, Mail, Scale, Phone,
  HardDrive, KeyRound, ClipboardList,
  Instagram, Facebook, Linkedin, Youtube,
} from "lucide-react";
import { ScrollingStrip, FAQItem, scrollTo } from "./LandingHelpers";
import { INTEGRATIONS, COMPETITORS, COMPARE_DATA, buildContent } from "./LandingContent";

export default function LandingBottom({ c, lang, handleCTA, handleLogin, bp, ep, iu, setLang, exampleStr }) {
  return (
    <>
      {/* FAQ */}
      <section className="py-20 sm:py-28 bg-slate-50" data-testid="landing-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#3B82F6] text-xs font-semibold px-4 py-1.5 rounded-full border border-blue-100 mb-4">
              <HelpCircle className="w-3.5 h-3.5" />{c.faq.badge}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-4">{c.faq.title}</h2>
            <p className="text-lg text-slate-500">{c.faq.sub}</p>
          </div>
          <div className="space-y-3">
            {c.faq.items.map((item, i) => <FAQItem key={i} item={item} />)}
          </div>
          <div className="text-center mt-10">
            <button onClick={handleCTA}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#3B82F6] text-white rounded-xl font-semibold text-sm hover:bg-blue-600 transition-all shadow-lg shadow-blue-200 hover:-translate-y-0.5">
              {c.nav.cta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* INTEGRATION STRIP */}
      <div className="bg-slate-900 py-10 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 mb-6 text-center">
          <p className="text-sm font-semibold text-slate-400 uppercase tracking-widest">
            {lang === "pt-BR" ? "Conecta com as ferramentas que voc\u00ea j\u00e1 usa" : lang === "en-US" ? "Connects with tools you already use" : "Conecta con las herramientas que ya usas"}
          </p>
        </div>
        <ScrollingStrip
          items={INTEGRATIONS} speed={28} bg="bg-slate-900" border={false}
          renderItem={(item) => (
            <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 hover:border-slate-500 transition-colors">
              <span className="text-base leading-none">{item.icon}</span>
              <span className="text-sm font-medium text-slate-300 whitespace-nowrap">{item.name}</span>
            </div>
          )}
        />
        <div className="max-w-4xl mx-auto px-4 mt-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            {[
              { value: "< 4h",   label: lang === "pt-BR" ? "Tempo de resposta" : lang === "en-US" ? "Response time" : "Tiempo de respuesta" },
              { value: "99,9%",  label: lang === "pt-BR" ? "Uptime garantido"  : lang === "en-US" ? "Guaranteed uptime" : "Uptime garantizado" },
              { value: "LGPD",   label: lang === "pt-BR" ? "100% em conformidade" : lang === "en-US" ? "100% compliant" : "100% conforme" },
              { value: "SSL",    label: lang === "pt-BR" ? "Dados criptografados" : lang === "en-US" ? "Encrypted data" : "Datos cifrados" },
            ].map((s, i) => (
              <div key={i} className="py-3">
                <p className="text-xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* COMPARISON TABLE */}
      <section className="py-20 sm:py-28 bg-white" data-testid="landing-compare">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-4">{c.compare.title}</h2>
            <p className="text-lg text-slate-500">{c.compare.sub}</p>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-5 py-4 text-left text-sm font-semibold text-slate-500 w-48"></th>
                  {COMPETITORS.map((comp, i) => (
                    <th key={comp} className={`px-5 py-4 text-center text-sm font-bold ${i === 0 ? "text-[#3B82F6]" : "text-slate-500"}`}>
                      {i === 0 ? (
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs bg-[#3B82F6] text-white px-2 py-0.5 rounded-full">
                            {lang === "pt-BR" ? "Voc\u00ea est\u00e1 aqui" : lang === "en-US" ? "You are here" : "T\u00fa est\u00e1s aqu\u00ed"}
                          </span>
                          {comp}
                        </div>
                      ) : comp}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.compare.features.map((feat, fi) => (
                  <tr key={fi} className={`border-b border-slate-100 ${fi % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}>
                    <td className="px-5 py-3.5 text-sm font-medium text-slate-700">{feat}</td>
                    {COMPARE_DATA[fi] === null ? (
                      <>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-base font-bold text-[#3B82F6]">{c.compare.prices[0]}</span>
                        </td>
                        {c.compare.prices.slice(1).map((p, i) => (
                          <td key={i} className="px-5 py-3.5 text-center text-sm text-slate-400">{p}</td>
                        ))}
                      </>
                    ) : (
                      COMPARE_DATA[fi].map((val, ci) => (
                        <td key={ci} className={`px-5 py-3.5 text-center ${ci === 0 ? "bg-blue-50/50" : ""}`}>
                          {val ? (
                            <Check className={`w-5 h-5 mx-auto ${ci === 0 ? "text-[#3B82F6]" : "text-emerald-500"}`} />
                          ) : (
                            <X className="w-4 h-4 mx-auto text-slate-300" />
                          )}
                        </td>
                      ))
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-center mt-8">
            <button onClick={handleCTA}
              className="inline-flex items-center gap-2 px-7 py-3 bg-[#3B82F6] text-white rounded-xl font-semibold text-sm hover:bg-blue-600 transition-all shadow-lg shadow-blue-200 hover:-translate-y-0.5">
              {c.nav.cta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 sm:py-28 bg-slate-50 scroll-mt-16" data-testid="landing-pricing">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-4">{c.pricing.title}</h2>
            <p className="text-lg text-slate-500">{c.pricing.sub}</p>
          </div>
          <div className="relative bg-white rounded-3xl border-2 border-[#3B82F6] shadow-xl overflow-visible pt-5">
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
              <span className="bg-[#3B82F6] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-sm">{c.pricing.badge}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-0 pt-8">
              <div className="text-center px-8 pb-6 sm:border-r border-slate-100">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  {lang === "pt-BR" ? "Plano base \u00b7 1 usu\u00e1rio" : lang === "en-US" ? "Base plan \u00b7 1 user" : "Plan base \u00b7 1 usuario"}
                </p>
                <div className="flex items-end justify-center gap-1 mb-1">
                  <span className="text-5xl font-extrabold text-slate-900">{c.pricing.basePrice}</span>
                </div>
                <p className="text-sm text-slate-400">{c.pricing.basePeriod}</p>
              </div>
              <div className="text-center px-8 pb-6 bg-blue-50/40">
                <p className="text-xs font-bold text-[#3B82F6] uppercase tracking-wider mb-2">
                  {lang === "pt-BR" ? "Usu\u00e1rios adicionais" : lang === "en-US" ? "Additional users" : "Usuarios adicionales"}
                </p>
                <div className="flex items-end justify-center gap-1 mb-1">
                  <span className="text-5xl font-extrabold text-[#3B82F6]">{c.pricing.extraPrice}</span>
                </div>
                <p className="text-sm text-slate-400">{c.pricing.extraPeriod}</p>
              </div>
            </div>
            <div className="mx-6 sm:mx-8 mb-6 bg-slate-50 rounded-xl px-4 py-3 border border-slate-200 text-center">
              <p className="text-xs text-slate-500">{c.pricing.example}</p>
            </div>
            <div className="px-6 sm:px-8 pb-8">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">
                {lang === "pt-BR" ? "Tudo incluso" : lang === "en-US" ? "Everything included" : "Todo incluido"}
              </p>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {c.pricing.features.map((feat, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <div className="w-4 h-4 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-2.5 h-2.5 text-[#3B82F6]" />
                    </div>
                    {feat}
                  </div>
                ))}
              </div>
              <button onClick={handleCTA}
                className="w-full mt-8 py-3.5 bg-[#3B82F6] text-white rounded-xl font-semibold text-base hover:bg-blue-600 transition-all shadow-lg shadow-blue-200 hover:shadow-xl hover:-translate-y-0.5">
                {c.pricing.cta} <ArrowRight className="inline w-4 h-4 ml-1" />
              </button>
              <p className="text-center text-xs text-slate-400 mt-3">{c.pricing.hint}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-28 bg-[#3B82F6] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-800/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
            <Star className="w-3.5 h-3.5 fill-current" />
            {lang === "pt-BR" ? `${bp}/m\u00eas \u00b7 ${iu} usu\u00e1rio incluso \u00b7 Todos os recursos` : lang === "en-US" ? `${bp}/month \u00b7 ${iu} user included \u00b7 All features` : `${bp}/mes \u00b7 ${iu} usuario incluido \u00b7 Todas las funciones`}
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold text-white font-heading mb-4">{c.finalCta.title}</h2>
          <p className="text-lg text-blue-100 mb-8">{c.finalCta.sub}</p>
          <button onClick={handleCTA} data-testid="final-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#3B82F6] rounded-xl font-bold text-base hover:bg-slate-50 transition-all shadow-xl hover:-translate-y-0.5">
            {c.finalCta.cta} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-400 py-14" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-start justify-between gap-10 mb-10">
            <div className="space-y-4 max-w-xs">
              <div className="flex items-center">
                <img src="/brand/icon-3.png" alt="Pipe4.me" style={{ height: "3.5rem" }} className="object-contain" />
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">O Pipe4.me é um CRM de vendas que organiza e acelera o seu processo comercial, conectando leads, clientes e oportunidades em um só lugar. Simples, intuitivo e eficiente, para gerenciar seu pipeline, acompanhar negociações e fechar mais negócios com controle total!</p>
              <div className="flex items-center gap-2 pt-1">
                {[
                  { href: "https://www.instagram.com/satellitustecnologia/", Icon: Instagram, label: "Instagram" },
                  { href: "https://web.facebook.com/satellitustecnologia", Icon: Facebook, label: "Facebook" },
                  { href: "https://www.linkedin.com/company/satellitus", Icon: Linkedin, label: "LinkedIn" },
                  { href: "https://www.youtube.com/channel/UCosSzT9DJdsf6H_oZIQ4t6Q", Icon: Youtube, label: "YouTube" },
                ].map(({ href, Icon, label }) => (
                  <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-colors">
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </a>
                ))}
              </div>
              <div className="text-[11px] text-slate-600 space-y-1 leading-relaxed">
                <p className="text-slate-500 font-medium mt-1">Satellitus Tecnologia S.A.</p>
                <p>CNPJ: 24.654.821/0001-00</p>
                <p>Av. Jundiaí, 1184 · Jundiaí - SP</p>
                <p>Brasil · CEP 13208-053</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-sm flex-1">
              <div className="space-y-2">
                <p className="text-white font-semibold text-xs uppercase tracking-wider mb-3">Produto</p>
                {[
                  { label: c.nav.howItWorks, id: "como-funciona" },
                  { label: c.nav.features,   id: "recursos" },
                  { label: c.nav.pricing,    id: "pricing" },
                ].map(item => (
                  <button key={item.id} onClick={() => scrollTo(item.id)}
                    className="block text-slate-400 hover:text-white transition-colors text-xs">{item.label}</button>
                ))}
              </div>
              <div className="space-y-2">
                <p className="text-white font-semibold text-xs uppercase tracking-wider mb-3">{lang === "pt-BR" ? "Empresa" : lang === "en-US" ? "Company" : "Empresa"}</p>
                <button onClick={() => scrollTo("quem-somos")}
                  className="block text-slate-400 hover:text-white transition-colors text-xs">{c.nav.about}</button>
              </div>
              <div className="space-y-2">
                <p className="text-white font-semibold text-xs uppercase tracking-wider mb-3">{lang === "pt-BR" ? "Conta" : lang === "en-US" ? "Account" : "Cuenta"}</p>
                <button onClick={handleLogin} className="block text-slate-400 hover:text-white transition-colors text-xs">{c.nav.login}</button>
                <button onClick={handleCTA} className="block text-[#3B82F6] hover:text-blue-400 transition-colors text-xs font-semibold">{c.nav.cta}</button>
              </div>
              <div className="space-y-2">
                <p className="text-white font-semibold text-xs uppercase tracking-wider mb-3">{lang === "pt-BR" ? "Contato" : lang === "en-US" ? "Contact" : "Contacto"}</p>
                <a href="mailto:suporte@pipe4.me" className="block text-slate-400 hover:text-white transition-colors text-xs">
                  <span className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-500" />{lang === "pt-BR" ? "Suporte" : lang === "en-US" ? "Support" : "Soporte"}</span>
                  <span className="block text-slate-600 text-[11px] ml-4.5">suporte@pipe4.me</span>
                </a>
                <a href="mailto:vendas@pipe4.me" className="block text-slate-400 hover:text-white transition-colors text-xs mt-2">
                  <span className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-slate-500" />{lang === "pt-BR" ? "Comercial" : lang === "en-US" ? "Sales" : "Ventas"}</span>
                  <span className="block text-slate-600 text-[11px] ml-4.5">vendas@pipe4.me</span>
                </a>
                <a href="mailto:legal@pipe4.me" className="block text-slate-400 hover:text-white transition-colors text-xs mt-2">
                  <span className="flex items-center gap-1.5"><Scale className="w-3 h-3 text-slate-500" />{lang === "pt-BR" ? "Jur\u00eddico / DPO" : lang === "en-US" ? "Legal / DPO" : "Legal / DPO"}</span>
                  <span className="block text-slate-600 text-[11px] ml-4.5">legal@pipe4.me</span>
                </a>
                <div className="block text-slate-400 text-xs mt-2">
                  <span className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-slate-500" />{lang === "pt-BR" ? "Telefone" : lang === "en-US" ? "Phone" : "Tel\u00e9fono"}</span>
                  <span className="block text-slate-600 text-[11px] ml-4.5">(11) 4230-0152</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-6 mb-6">
            <p className="text-[10px] text-slate-600 uppercase tracking-widest mb-3">
              {lang === "pt-BR" ? "Seguran\u00e7a & Conformidade" : lang === "en-US" ? "Security & Compliance" : "Seguridad & Conformidad"}
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { Icon: Lock,          label: "SSL / TLS",         sub: "Criptografia em tr\u00e2nsito" },
                { Icon: Shield,        label: "LGPD Compliant",    sub: "Lei n\u00ba 13.709/2018" },
                { Icon: Building2,     label: "Multi-tenant",      sub: "Dados isolados por empresa" },
                { Icon: ClipboardList, label: "Auditoria Completa", sub: "Log de todas as a\u00e7\u00f5es" },
                { Icon: HardDrive,     label: "Backup Autom\u00e1tico", sub: "Recupera\u00e7\u00e3o garantida" },
                { Icon: KeyRound,      label: "Controle de Acesso",sub: "Permiss\u00f5es granulares" },
              ].map(({ Icon, label, sub }, i) => (
                <div key={i}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg hover:border-slate-500 transition-colors">
                  <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] font-semibold text-slate-300">{label}</p>
                    <p className="text-[10px] text-slate-600">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-slate-800 pt-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <p className="text-xs text-slate-600">\u00a9 2026 Satellitus Tecnologia S.A. \u00b7 Pipe4.me</p>
              <a href="/termos-de-uso" className="text-xs text-slate-500 hover:text-slate-300 transition-colors hover:underline underline-offset-2">
                {lang === "pt-BR" ? "Termos de Uso" : lang === "en-US" ? "Terms of Use" : "T\u00e9rminos de Uso"}
              </a>
              <a href="/politica-de-privacidade" className="text-xs text-slate-500 hover:text-slate-300 transition-colors hover:underline underline-offset-2">
                {lang === "pt-BR" ? "Pol\u00edtica de Privacidade" : lang === "en-US" ? "Privacy Policy" : "Pol\u00edtica de Privacidad"}
              </a>
            </div>
            <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
              {Object.entries(buildContent(bp, ep, iu, exampleStr)).map(([key, val]) => (
                <button key={key} onClick={() => setLang(key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${lang === key ? "bg-slate-700 text-white" : "text-slate-500 hover:text-slate-300"}`}>
                  {val.flag} {val.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
